import React, { memo, useState, Suspense, useRef, useEffect, useCallback } from 'react';
import { Handle, Position, useReactFlow, useStore } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Button, message, Card, Input, Tooltip } from 'antd';
import { LoadingOutlined, PictureOutlined, GatewayOutlined, FullscreenOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';
import { useGeneration } from '../../../context/GenerationContext';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import { NODE_BG, TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import PanoramaViewer from '../components/PanoramaViewer';

const NEON_CYAN = '#00f3ff';

const HunyuanPanoramaNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();
    const [prompt, setPrompt] = useState(data.prompt || '');
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Get connected input image
    const connectedInput = useStore(
        useCallback((s) => {
            const edge = s.edges.find((e) => e.target === id && e.targetHandle === 'input-image');
            if (!edge) return null;
            const sourceNode = s.nodeInternals.get(edge.source);
            return sourceNode?.data?.src || null;
        }, [id])
    );

    const inputImage = connectedInput || data.inputImage || null;
    const panoramaUrl = data.panoramaUrl || null;

    const updateData = useCallback((keyOrObj, value) => {
        let newValues = keyOrObj;
        if (typeof keyOrObj === 'string') {
            newValues = { [keyOrObj]: value };
        }

        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return { ...node, data: { ...node.data, ...newValues } };
            }
            return node;
        }));
        if (data.onSave) data.onSave(id, newValues);
    }, [id, setNodes, data]);

    const handleGenerate = async () => {
        if (!inputImage) {
            message.warning('Please connect an image first.');
            return;
        }
        if (!prompt) {
            message.warning('Please enter a prompt.');
            return;
        }

        setGenerating(true);
        try {
            const response = await api.post('/fal/hunyuan/world', {
                image_url: inputImage,
                prompt: prompt,
                canvas_id: canvasId,
                node_id: id
            });

            const { request_id } = response.data;
            const modelId = 'fal-ai/hunyuan_world';

            // setGenerating(false); // REMOVED

            trackGeneration(request_id, modelId, {
                prompt: 'Hunyuan Panorama',
                type: 'Image',
                canvas_id: canvasId,
                node_id: id,
                promptProp: prompt, // store actual prompt text for saving
                is_panorama: true
            }, {
                onComplete: (result) => {
                    if (result && result.image && result.image.url) {
                        const finalUrl = result.image.url;
                        // Batch update to prevent race conditions
                        updateData({
                            panoramaUrl: finalUrl,
                            src: finalUrl
                        });
                        message.success('Panorama Generated!');
                    } else {
                        message.error('Generation failed - no image returned.');
                    }
                    setGenerating(false); // Stop glow
                },
                onFail: (err) => {
                    console.error("Panorama Generation failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error('Panorama generation error:', error);
            message.error('Generation failed.');
            setGenerating(false); // Stop glow if submission fails
        }
    };

    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    // Handle authenticated URL proxying
    const getAuthenticatedUrl = useCallback((url) => {
        if (!url) return null;
        if (!token) return url;
        return `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(url)}&token=${token}`;
    }, [token]);

    const renderViewer = (isFull = false) => {
        if (!panoramaUrl || !token) {
            return (
                <div style={{
                    height: '100%', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#444', opacity: 0.6
                }}>
                    <PictureOutlined style={{ fontSize: 48, marginBottom: 12 }} />
                    <div>No Panorama</div>
                </div>
            );
        }

        const authUrl = getAuthenticatedUrl(panoramaUrl);

        return (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <PanoramaViewer imageUrl={authUrl} />

                {/* Overlay Controls */}
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
                    {!isFull ? (
                        <Tooltip title="Fullscreen">
                            <Button size="small" icon={<FullscreenOutlined />} onClick={() => setIsFullscreen(true)} ghost />
                        </Tooltip>
                    ) : (
                        <Button
                            type="text"
                            icon={<LogoutOutlined />}
                            style={{ color: '#fff', fontSize: 16 }}
                            onClick={() => setIsFullscreen(false)}
                        >
                            Exit Fullscreen
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer isVisible={selected} minWidth={400} minHeight={400} onResizeEnd={(e, p) => data.onResize && data.onResize(id, p)} />

            <NodeLabel
                title={data.nodeTitle || "Hunyuan World"}
                iconType="ant-design"
                icon={<PictureOutlined />}
                color={NEON_CYAN}
                onTitleChange={onTitleChange}
            />

            {/* Input Handle */}
            <Handle
                type="target"
                position={Position.Left}
                id="input-image"
                style={{ ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible), top: '50%' }}
            />

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                id="output-image"
                style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', background: NODE_BG }}
                style={{ width: '100%', height: '100%', border: '1px solid #30363d', background: NODE_BG, overflow: 'hidden', borderRadius: 8 }}
            >
                {/* 360 Viewer Area */}
                <div className="nodrag" style={{ flex: 1, background: '#000', position: 'relative', minHeight: 0 }}>
                    {renderViewer(false)}
                </div>

                {/* Controls Area */}
                <div className="nodrag" style={{
                    padding: 12,
                    background: 'rgba(19, 22, 28, 0.95)',
                    borderTop: '1px solid #30363d'
                }}>
                    <Input.TextArea
                        placeholder="Enter prompt (e.g. A skyland of wonders)"
                        value={prompt}
                        onChange={(e) => {
                            setPrompt(e.target.value);
                            updateData('prompt', e.target.value); // Optimistically update local data
                        }}
                        onBlur={(e) => updateData('prompt', e.target.value)} // Ensure save on blur
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        style={{ marginBottom: 8, fontSize: 12 }}
                    />

                    <Button
                        type="primary"
                        block
                        icon={generating ? <LoadingOutlined /> : <GatewayOutlined />}
                        onClick={handleGenerate}
                        disabled={generating || !inputImage || !prompt}
                        style={{
                            background: generating ? '#222' : NEON_CYAN,
                            borderColor: generating ? '#444' : NEON_CYAN,
                            color: generating ? '#666' : '#000',
                            fontWeight: 600
                        }}
                    >
                        {generating ? 'GENERATING...' : 'GENERATE PANORAMA'}
                    </Button>
                </div>
            </Card>

            {/* Fullscreen Modal */}
            {isFullscreen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    zIndex: 2000, background: '#000'
                }}>
                    {renderViewer(true)}
                </div>
            )}
        </div>
    );
};

export default memo(HunyuanPanoramaNode);
